import { Fragment } from "react";
import { Upload } from "antd";
import classNames from "classnames";
import { ClipUploadFileCard } from "./ClipUploadFileCard";
import { ClipUploadImagePreview } from "./ClipUploadImagePreview";
import { ClipUploadPlaceholder } from "./ClipUploadPlaceholder";
import { DEFAULT_CARD_HEIGHT, DEFAULT_CARD_WIDTH, DEFAULT_GAP } from "./constants";
import type { ClipUploadProps } from "./types";
import { useClipUpload } from "./useClipUpload";
import { useClipUploadPreview } from "./useClipUploadPreview";

export type {
  ClipUploadFile,
  ClipUploadImageCompressOptions,
  ClipUploadContext,
  ClipUploadProps,
  ClipUploadRenderHelpers,
} from "./types";

export const ClipUpload = ({
  files,
  defaultFiles,
  onFilesChange,
  uploadFile,
  beforeUpload,
  maxCount = 1,
  accept,
  onUnsupportedFileType,
  compressImage = false,
  showProgress = true,
  disabled = false,
  cardWidth = DEFAULT_CARD_WIDTH,
  cardHeight = DEFAULT_CARD_HEIGHT,
  gap = DEFAULT_GAP,
  placeholderText,
  hint,
  uploadPlaceholder,
  className,
  renderUploadedFile,
}: ClipUploadProps) => {
  const {
    currentFiles,
    uploadingItems,
    previewUrls,
    showPlaceholder,
    acceptString,
    handleRemoveDone,
    handleRemoveUploading,
    handleBeforeUpload,
    handleCustomRequest,
    handleDrop,
  } = useClipUpload({
    files,
    defaultFiles,
    onFilesChange,
    uploadFile,
    beforeUpload,
    maxCount,
    accept,
    onUnsupportedFileType,
    compressImage,
    disabled,
  });

  const { getPreviewHandler, imagePreview } = useClipUploadPreview(previewUrls);

  return (
    <div className={classNames("flex flex-wrap items-start", className)} style={{ gap }}>
      {currentFiles.map((file) => {
        const remove = () => handleRemoveDone(file.uid);
        const preview = getPreviewHandler(file);

        if (renderUploadedFile) {
          return (
            <Fragment key={file.uid}>
              {renderUploadedFile(file, {
                remove: disabled ? () => {} : remove,
                preview,
                width: cardWidth,
                height: cardHeight,
              })}
            </Fragment>
          );
        }

        return (
          <ClipUploadFileCard
            key={file.uid}
            name={file.name}
            type={file.type}
            url={previewUrls[file.uid] ?? file.url}
            thumbUrl={file.thumbUrl}
            status="done"
            width={cardWidth}
            height={cardHeight}
            showProgress={showProgress}
            disabled={disabled}
            onRemove={disabled ? undefined : remove}
            onPreview={preview}
          />
        );
      })}
      {uploadingItems.map((item) => (
        <ClipUploadFileCard
          key={item.uid}
          name={item.name}
          type={item.type}
          url={item.previewUrl}
          status={item.status}
          percent={item.percent}
          errorMessage={item.errorMessage}
          width={cardWidth}
          height={cardHeight}
          showProgress={showProgress}
          disabled={disabled}
          onRemove={() => handleRemoveUploading(item.uid)}
        />
      ))}
      {showPlaceholder ? (
        <div className="shrink-0" style={{ width: cardWidth, height: cardHeight }}>
          <Upload
            type="drag"
            multiple={maxCount > 1}
            pastable
            accept={acceptString || undefined}
            disabled={disabled}
            showUploadList={false}
            beforeUpload={handleBeforeUpload}
            customRequest={handleCustomRequest}
            onDrop={handleDrop}
            classNames={{
              // 孙子级 span 标签的 padding 设置为 0
              root: "[&>*>span]:p-0! h-full! w-full! border-0! bg-transparent! p-0!",
              trigger: "h-full! w-full! border-0! bg-transparent! p-0!",
            }}
          >
            <ClipUploadPlaceholder
              disabled={disabled}
              placeholderText={placeholderText}
              hint={hint}
              uploadPlaceholder={uploadPlaceholder}
            />
          </Upload>
        </div>
      ) : null}
      <ClipUploadImagePreview {...imagePreview} />
    </div>
  );
};
